import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useAuth } from "../../context/AuthContext";
import { getClubInvoices, type ClubInvoice } from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import ClubContracts from "../../components/club/ClubContracts";
import { SectionCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import { formatDate } from "../../lib/dateFormat";
import { invoiceDocumentNumber, invoiceDocumentLabel, isFiscalInvoice } from "../../lib/invoiceDocument";

export default function ClubInvoices() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();

  const [invoices, setInvoices] = useState<ClubInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeClubId) return;

    (async () => {
      setLoading(true);
      try {
        const data = await getClubInvoices(activeClubId);
        setInvoices(data);
      } catch (error) {
        console.error("Failed to load invoices:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid": return "bg-emerald-100 text-emerald-700";
      case "issued": return "bg-blue-100 text-blue-700";
      case "overdue": return "bg-red-100 text-red-700";
      case "draft": return "bg-slate-100 text-slate-700";
      case "cancelled": return "bg-slate-100 text-slate-500";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  const sumBy = (pred: (i: ClubInvoice) => boolean) =>
    invoices.filter(pred).reduce((a, i) => a + (i.amount_cents || 0), 0);
  const paidCents = sumBy((i) => i.status === "paid");
  const dueCents = sumBy((i) => i.status === "open" || i.status === "draft");
  const overdueCents = sumBy((i) => i.status === "uncollectible");
  const eur = (c: number) => `${(c / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        {/* The old copy said "Facturile clubului către iGROWth", which had the
            invoice pointing the wrong way: the club is the one being billed. */}
        <AdminPageHeader
          title={t("admin.invoices", "Facturi")}
          subtitle={t(
            "clubInvoices.subtitle",
            "Facturile lunare de achitat către iGROWth – Sportiv Educat, pentru abonamentul clubului."
          )}
          icon={<ClubNavIcon id="invoices" className="w-6 h-6" />}
        />

        {invoices.length > 0 && (
          <StatGrid cols={3}>
            <StatCard label={t("clubInvoices.paid", "Achitat")} value={eur(paidCents)} dotClass="bg-emerald-500" />
            <StatCard label={t("clubInvoices.due", "De achitat")} value={eur(dueCents)} dotClass="bg-amber-500" />
            <StatCard label={t("clubInvoices.overdue", "Restant")} value={eur(overdueCents)} dotClass="bg-rose-500" valueClass={overdueCents > 0 ? "text-rose-600" : "text-slate-800"} />
          </StatGrid>
        )}

        <SectionCard
          title={t("clubInvoices.all", "Toate facturile")}
          subtitle={`${invoices.length} ${t("common.total", "total")}`}
        >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("invoice.number", "Factură #")}</th>
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("invoice.period", "Perioadă")}</th>
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("invoice.amount", "Sumă")}</th>
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("common.status", "Status")}</th>
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("invoice.athletes", "Sportivi")}</th>
                <th className="px-4 py-3 text-sm font-medium text-left text-slate-500">{t("common.actions", "Acțiuni")}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-800">{invoiceDocumentNumber(invoice)}</span>
                    {!isFiscalInvoice(invoice) && (
                      <span className="ml-2 text-[11px] font-semibold text-amber-600">Proformă</span>
                    )}
                    <span className="block text-xs text-slate-400">{formatDate(new Date(invoice.created_at))}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {invoice.period_start && invoice.period_end ? (
                      <>
                        {formatDate(new Date(invoice.period_start))} - {formatDate(new Date(invoice.period_end))}
                      </>
                    ) : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-800">
                      {invoice.amount_cents != null ? (invoice.amount_cents / 100).toFixed(2) : "0.00"} {invoice.currency?.toUpperCase() || "EUR"}
                    </span>
                    {invoice.amount_ron_cents != null && (
                      <span className="block text-xs text-slate-400">
                        ({(invoice.amount_ron_cents / 100).toFixed(2)} RON)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {invoice.status === "open" ? t("invoice.pending", "În așteptare") : invoice.status}
                    </span>
                    {invoice.paid_at && (
                      <span className="block mt-1 text-xs text-slate-400">
                        {t("invoice.paidAt", "Plătit")}: {formatDate(new Date(invoice.paid_at))}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600">
                    {invoice.athlete_count ?? "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {/* Download PDF */}
                      <a
                        href={`${process.env.REACT_APP_API_URL || "https://igapp-production.up.railway.app"}/invoices/${invoice.id}/pdf?token=${encodeURIComponent(Cookies.get("igrowth_token") || "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title={t("invoice.downloadPdf", "Descarcă PDF")}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </a>
                      {/* Payment Link - only show if not paid */}
                      {invoice.stripe_payment_link && invoice.status !== "paid" && (
                        <a
                          href={invoice.stripe_payment_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
                          title={t("invoice.pay", "Plătește acum")}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          {t("invoice.pay", "Plătește")}
                        </a>
                      )}
                      {/* View on Stripe */}
                      {invoice.hosted_invoice_url && (
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                          title={t("invoice.viewOnline", "Vezi online")}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && (
            <div className="py-14 px-5 text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.6a1 1 0 01.7.3l4.4 4.4a1 1 0 01.3.7V19a2 2 0 01-2 2z" /></svg>
              </div>
              <h3 className="font-semibold text-slate-700">{t("admin.noInvoices", "Nicio factură încă")}</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{t("clubInvoices.emptyHint", "iGROWth – Sportiv Educat emite factura la finalul fiecărei luni, calculată după numărul de sportivi activi din club.")}</p>
            </div>
          )}
        </div>
        </SectionCard>

        <ClubContracts clubId={activeClubId} />
      </div>
    </ClubAdminLayout>
  );
}
